import { IntlShape } from "react-intl";
import { ExtraCategory } from "../../domain/ExtraCategory.enum";
import {
  CategoryExtraPrice,
  ExtraRateType,
  ExtraUnitType,
} from "../../domain/entities/CategoryExtraLine";
import { categoryLimitsMessages } from "../components/CategoryLimitsField/CategoryLimitsField.messages";
import {
  categoryLabelMessages,
  categoryPluralMessages,
  categorySingularMessages,
  rateTypeMessages,
  unitTypeMessages,
} from "../labels.messages";

type AssignableCategory = keyof typeof categoryPluralMessages;

const isAssignable = (category: ExtraCategory): category is AssignableCategory =>
  category in categoryPluralMessages;

/** Display texts of the *Limits per category* section, in the user locale. */
export class CategoryLimitsLabels {
  constructor(private readonly intl: IntlShape, private readonly currency?: string) {}

  /** "vehicles", as in *Limit vehicles in this accommodation*. */
  categoryPlural(category: ExtraCategory): string {
    return this.intl.formatMessage(
      isAssignable(category) ? categoryPluralMessages[category] : categoryLabelMessages[category]
    );
  }

  /** "vehicle", as in *the first vehicle free of charge*. */
  categorySingular(category: ExtraCategory): string {
    return this.intl.formatMessage(
      isAssignable(category) ? categorySingularMessages[category] : categoryLabelMessages[category]
    );
  }

  /** The [accommodation] of the limit help texts: the typed name, or the generic word. */
  accommodation(name: string): string {
    return name.trim() || this.intl.formatMessage(categoryLimitsMessages.accommodationFallback);
  }

  /** Unit type of a line; an unspecified unit type shows nothing. */
  unitType(unitType: ExtraUnitType): string {
    return unitType === ExtraUnitType.UNSPECIFIED
      ? ""
      : this.intl.formatMessage(unitTypeMessages[unitType]);
  }

  /** Price of a line: the amount in the property currency, or the rate type label. */
  price(price: CategoryExtraPrice): string {
    if (price.constantAmount !== undefined) {
      return this.amount(price.constantAmount);
    }
    if (price.rateType !== undefined && price.rateType !== ExtraRateType.UNSPECIFIED) {
      return this.intl.formatMessage(rateTypeMessages[price.rateType]);
    }
    return "";
  }

  private amount(value: number): string {
    const options: Intl.NumberFormatOptions = this.currency
      ? { style: "currency", currency: this.currency }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    try {
      return new Intl.NumberFormat(this.intl.locale, options).format(value);
    } catch {
      // An unknown currency code must not break the page.
      return new Intl.NumberFormat(this.intl.locale, { minimumFractionDigits: 2 }).format(value);
    }
  }
}
